import { useState } from 'react';
import { Link, useSearch } from 'wouter';
import { api, type FinanceAccounts, type FinanceFindings, type FinanceMonthly, type Finding } from '../lib/api';
import type { ConfirmRequest } from '../components/Confirm';
import { useResource } from '../lib/use-resource';
import { aud, nzd, monthLong, monthShort } from '../lib/finance';
import './finance.css';

/**
 * The monthly wholesale audit (Finance F3): what Hero billed that does not add up, as cards.
 *
 * Every finding is a wholesale_finding card, decided like any other: Dismiss means known and acceptable; a note
 * "Raised with Hero" keeps it from being raised again for the months settings say. The rules are in the PSA repo
 * (lib/finance/findings.js), one per kind; nothing here decides anything.
 */
const KIND_LABEL: Record<string, string> = {
  billed_inactive: 'Billed on a cancelled or suspended account',
  orphan_line: 'Lines that belong to no account',
  port_fee_unrecovered: 'Port fees not charged to the customer',
  fine_or_penalty: 'Fines and penalties',
  channel_mismatch: 'Channels that do not match',
  trial_cost: 'What trials cost',
  negative_margin: 'Paying accounts that lost money',
  unreconciled: "Invoice against Hero's lines",
  refund_not_reversed: 'Refunds still credited',
  cash_mismatch: 'Cash in only one of Stripe and the portal',
  portal_billing_cancelled: 'The portal still charging accounts we closed',
};

export function FinanceAudit({ onConfirm }: { onConfirm: (r: ConfirmRequest) => void }) {
  const search = new URLSearchParams(useSearch());
  const monthly = useResource<FinanceMonthly>(() => api.finance.monthly(), { pollMs: 300_000 });
  const [picked, setPicked] = useState<string | null>(search.get('period'));
  const period = picked ?? monthly.data?.verdict?.month ?? null;
  const on = { enabled: Boolean(period), deps: [period] };
  const findings = useResource<FinanceFindings>(() => api.finance.findings(period!), on);
  const accounts = useResource<FinanceAccounts>(() => api.finance.accounts(period!), on);
  const [error, setError] = useState<string | null>(null);

  if (!period) return <main className="fin"><p className="list-empty">{monthly.error?.message ?? 'Reading the months…'}</p></main>;
  const rows = monthly.data?.rows ?? [];
  const choices = rows.slice(-7, -1).reverse().map((r) => r.month);
  const act = async (fn: () => Promise<unknown>) => {
    setError(null);
    try { await fn(); } catch (e) { setError((e as Error).message); }
    findings.refetch();
  };
  const dismiss = (f: Finding) => onConfirm({
    title: 'Dismiss this finding as known and acceptable?', detail: f.title, confirmLabel: 'Dismiss',
    askReason: true, reasonRequired: true, reasonPlaceholder: 'why it is acceptable',
    onConfirm: (reason) => act(() => api.dismiss(f.id, reason)),
  });
  const raised = (f: Finding) => onConfirm({
    title: 'Record that this was raised with Hero?',
    detail: `${f.title}. It will not be raised again for this account and kind for the months set in settings.wholesale_finding_suppress_months.`,
    confirmLabel: 'Raised with Hero', askReason: true, reasonPlaceholder: 'ticket number or who you spoke to',
    onConfirm: (reason) => act(() => api.note(f.id, `Raised with Hero${reason ? `: ${reason}` : ''}`)),
  });
  const pn = findings.data?.paid_for_nothing;

  return (
    <main className="fin">
      <header className="fin-head">
        <div>
          <div className="fin-eyebrow mono"><Link href="/finance">Finance</Link> · wholesale audit</div>
          <h1>{monthLong(period)} · Hero's bill, checked</h1>
          <p className="fin-sentence">Every Hero line is mapped to an account; each rule's findings are cards, decided here or on the Desk.</p>
        </div>
        <div className="fin-switches">
          <div className="fin-seg mono" role="group" aria-label="Hero billing month">
            {choices.map((c) => (
              <button key={c} type="button" aria-pressed={c === period} onClick={() => setPicked(c)}>{monthShort(c)}</button>
            ))}
          </div>
        </div>
      </header>

      <div className="fin-cards fin-cards-3">
        <div className="fin-card is-alert-card">
          <div className="fin-label mono">Paid for nothing · {monthShort(period)}</div>
          <span className="fin-big">{pn ? (pn.month.aud == null ? nzd(pn.month.nzd) : aud(pn.month.aud, { cents: true })) : '…'}</span>
          <div className="fin-sub">{pn ? `${nzd(pn.month.nzd)} · ${pn.month.findings} finding${pn.month.findings === 1 ? '' : 's'}` : ''}</div>
        </div>
        <div className="fin-card">
          <div className="fin-label mono">Paid for nothing · six months</div>
          <span className="fin-big">{pn ? (pn.trailing_6.aud == null ? nzd(pn.trailing_6.nzd) : aud(pn.trailing_6.aud, { cents: true })) : '…'}</span>
          <div className="fin-sub">{pn ? `${findings.data?.from} to ${period} · ${nzd(pn.trailing_6.nzd)}` : ''}</div>
        </div>
        <div className="fin-card">
          <div className="fin-label mono">What counts</div>
          <div className="fin-sub">Billed on a cancelled or suspended account, lines that belong to no account, and port fees not charged on — at each month's FX rate.</div>
        </div>
      </div>

      {error && <p className="is-down">{error}</p>}

      <section className="fin-panel">
        <h2>Findings · {monthLong(period)}</h2>
        {!findings.data ? <p className="list-empty">{findings.error?.message ?? 'Reading…'}</p> : findings.data.by_kind.map(({ kind, findings: fs }) => (
          <div key={kind} className="aud-kind">
            <div className="aud-kind-head"><span>{KIND_LABEL[kind] ?? kind}</span><span className="mono fin-small">{fs.length || 'none'}</span></div>
            {fs.map((f) => (
              <article key={f.id} className={`aud-finding ${f.status === 'pending' ? 'is-open' : ''}`}>
                <div className="aud-title">
                  <span>{f.title}</span>
                  <span className="mono fin-small">{f.amount_nzd != null ? nzd(f.amount_nzd) : f.amount_aud != null ? aud(f.amount_aud, { cents: true }) : ''}</span>
                </div>
                <ul className="aud-evidence">{(f.evidence ?? []).map((e, i) => <li key={i}>{e.fact} <span className="fin-muted">· {e.observed ?? (e as unknown as { source?: string }).source}</span></li>)}</ul>
                <div className="aud-actions">
                  <Link href={`/cards/${f.id}`} className="fin-link">Card #{f.id}</Link>
                  {f.status === 'pending' ? (
                    <>
                      <button type="button" onClick={() => dismiss(f)}>Dismiss</button>
                      <button type="button" onClick={() => raised(f)}>Raised with Hero</button>
                    </>
                  ) : <span className="fin-muted">{f.status}{f.decided_by ? ` by ${f.decided_by}` : ''}</span>}
                </div>
              </article>
            ))}
          </div>
        ))}
      </section>

      <section className="fin-panel">
        <div className="fin-panel-head"><h2>Margin by account · {monthLong(period)}</h2><span className="mono fin-small">calls allocated by chargeable minutes</span></div>
        {!accounts.data ? <p className="list-empty">{accounts.error?.message ?? 'Reading…'}</p> : (
          <table className="fin-table">
            <thead className="mono"><tr><th>Account</th><th>Class</th><th>Charged</th><th>Hero</th><th>Card fees</th><th>Margin</th></tr></thead>
            <tbody>
              {accounts.data.accounts.map((a) => (
                <tr key={a.account_no}>
                  <td>{a.name ?? a.account_no} <span className="fin-muted">{a.name ? a.account_no : ''}</span></td>
                  <td>{a.class}</td>
                  <td>{aud(a.charged_ex, { cents: true })}{a.uncollectable_ex ? <div className="fin-muted fin-small">{a.uncollectable_ex === a.charged_ex ? 'charged, not collected' : `${aud(a.uncollectable_ex, { cents: true })} charged, not collected`}</div> : null}
                    {a.trial_credit_ex ? <div className="fin-muted fin-small">{aud(a.trial_credit_ex, { cents: true })} trial credit consumed</div> : null}</td>
                  <td title={nzd(a.hero_nzd_inc)}>{a.hero_aud == null ? 'no rate' : aud(a.hero_aud, { cents: true })}</td>
                  <td>{aud(a.fees_ex, { cents: true })}</td>
                  <td className={a.margin_aud != null && a.margin_aud < 0 ? 'is-down' : ''}>{a.margin_aud == null ? 'no rate' : aud(a.margin_aud, { cents: true })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </main>
  );
}
